import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PillButton } from '@ab-platform/ui/pill-button';
import { Checkbox } from '@ab-platform/ui/checkbox';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { Label } from '@ab-platform/ui/label';
import { AddressEntryFields } from '@/components/AddressEntryFields';
import {
  STEP_FIELDS,
  type RegistrationFormValues,
} from '@/hooks/useRegistrationForm';

/**
 * Step 3 — Indirizzi.
 *
 * Multi-address surface (PR-C). The slots rendered depend on
 * `organization.legalNature`:
 *   COMPANY → sede legale (always) + sede operativa (toggle) + sede corrispondenza (toggle)
 *   PERSON  → residenza/abitazione (always) + domicilio (toggle)
 *
 * Each slot is a 5-field block (Via / Città / Provincia / CAP / Paese)
 * with a HERE Maps autocomplete and a CountryPicker. The reusable
 * `AddressEntryFields` component owns the rendering and is fed by the
 * dotted form path passed in `path`.
 *
 * Required-ness is enforced at the form root via `superRefine` in
 * `useRegistrationForm.ts` so this component does not duplicate the
 * rules — clicking Continua runs `trigger('addresses')` which fans out
 * to every active slot.
 */
export function IndirizziPage(): JSX.Element {
  const navigate = useNavigate();
  const form = useFormContext<RegistrationFormValues>();
  const { control, trigger } = form;

  const legalNature = useWatch({ control, name: 'organization.legalNature' });
  const isPerson = legalNature === 'PERSON';

  const handleContinue = async (): Promise<void> => {
    const ok = await trigger(
      STEP_FIELDS.address as unknown as Parameters<typeof trigger>[0],
    );
    if (ok) navigate('/registrazione-intermediario/amministratore');
  };

  return (
    <FocusedPageShell
      title={isPerson ? 'Indirizzo di residenza' : 'Sede legale e altri indirizzi'}
      description={
        isPerson
          ? 'Cerca l\'indirizzo per compilarlo automaticamente, oppure inseriscilo a mano. Aggiungi un domicilio diverso solo se ti serve.'
          : 'Inserisci la sede legale. Se la sede operativa o l\'indirizzo di corrispondenza sono diversi dalla sede legale, aggiungili separatamente.'
      }
      onBack={() => navigate('/registrazione-intermediario/contatti')}
      footer={
        <PillButton
          type="button"
          size="lg"
          onClick={handleContinue}
          className="w-full"
        >
          Continua
        </PillButton>
      }
    >
      <AddressEntryFields
        path="addresses.primary"
        label={isPerson ? 'Residenza / Abitazione' : 'Sede legale'}
      />

      {isPerson ? (
        <ToggleableSlot
          flagPath="addresses.alternateResidence.enabled"
          dataPath="addresses.alternateResidence.data"
          checkboxLabel="Domicilio diverso dalla residenza"
          slotLabel="Domicilio"
        />
      ) : (
        <>
          <ToggleableSlot
            flagPath="addresses.alternateOffice.enabled"
            dataPath="addresses.alternateOffice.data"
            checkboxLabel="Sede operativa diversa dalla sede legale"
            slotLabel="Sede operativa"
          />
          <ToggleableSlot
            flagPath="addresses.mailingAddress.enabled"
            dataPath="addresses.mailingAddress.data"
            checkboxLabel="Indirizzo di corrispondenza diverso"
            slotLabel="Sede di corrispondenza"
          />
        </>
      )}
    </FocusedPageShell>
  );

  /**
   * Local helper component (closure over `register`/`control`) — keeps
   * the toggle wiring out of the parent's render body.
   */
  function ToggleableSlot({
    flagPath,
    dataPath,
    checkboxLabel,
    slotLabel,
  }: {
    flagPath:
      | 'addresses.alternateOffice.enabled'
      | 'addresses.mailingAddress.enabled'
      | 'addresses.alternateResidence.enabled';
    dataPath:
      | 'addresses.alternateOffice.data'
      | 'addresses.mailingAddress.data'
      | 'addresses.alternateResidence.data';
    checkboxLabel: string;
    slotLabel: string;
  }): JSX.Element {
    const enabled = useWatch({ control, name: flagPath });
    return (
      <div className="grid gap-4 rounded-lg border border-foreground/10 p-4">
        <Label className="flex items-start gap-3 text-sm font-normal text-foreground">
          <Controller
            control={control}
            name={flagPath}
            render={({ field }) => (
              <Checkbox
                className="mt-0.5"
                checked={field.value === true}
                onChange={(e) => field.onChange(e.currentTarget.checked)}
              />
            )}
          />
          <span>{checkboxLabel}</span>
        </Label>

        {enabled ? (
          <AddressEntryFields path={dataPath} label={slotLabel} />
        ) : null}
      </div>
    );
  }
}
